/**
 * The week grid, decided without React and without the network.
 *
 * Every rule the calendar runs on lives here so all of them can be tested
 * without mounting anything, the way `responseQueue.ts` carries the response
 * queue's rules. See docs/superpowers/specs/2026-08-14-schedule-week-design.md.
 */

import moment from 'moment'
import { toMinutes } from 'lib/dayClock'

/** Minutes past midnight, local wall clock. */
export type Minutes = number

/** A week is Monday to Friday. Practices with Saturday clinics are a later problem. */
const DAYS_IN_WEEK = 5

/**
 * The Monday of whatever week a date falls in.
 *
 * ISO weeks, so a Saturday belongs to the week that has just ended rather than
 * the one about to start. Somebody looking at Saturday's schedule is looking at
 * the week they are in.
 */
export function startOfWeek(date: moment.Moment | string = moment()): string {
  return moment(date).startOf(`isoWeek`).format(`YYYY-MM-DD`)
}

/** The five dates a week draws, from its Monday. */
export function weekDates(weekStart: string): string[] {
  const start = moment(weekStart)
  return Array.from({ length: DAYS_IN_WEEK }, (_, index) => start.clone().add(index, `day`).format(`YYYY-MM-DD`))
}

export type GridAppointment = {
  id: string
  /** YYYY-MM-DD, wall clock. */
  date: string
  /** HH:mm, wall clock. Not UTC: see rule 2 in the spec. */
  time: string
  /** Minutes. */
  duration?: number | null
  operatoryId?: string | null
  providerId?: string | null
  patientName?: string | null
  /** What the appointment is for, as the practice typed it. */
  reason?: string | null
  cancelled?: boolean | null
  broken?: boolean | null
  completed?: boolean | null
  confirmed?: boolean | null
}

/** One opening, as the availability engine reported it. */
export type GridSlot = {
  date: string
  /**
   * Wall clock, in whatever shape the engine sent.
   *
   * Measured against Hamblin: the engine sends a display string,
   * `"1:00 PM - Thursday, August 20, 2026"`, not `HH:mm`. Read `start` on the
   * merged slot for a position; `slotMinutes` is what turns one into the other.
   */
  time: string
  /** How many chairs are open at this time. */
  count: number
  /**
   * Appointment category key, e.g. `general` or `limitedExam`.
   *
   * A category the practice mapped one of its own appointment types onto in the
   * AI agent settings, not the name of that type: the availability endpoint
   * resolves the category and does not say what it resolved to.
   */
  type: string
  typeLabel: string
  /** Minutes this type of appointment takes, when the caller knows it. */
  duration?: number | null
}

/**
 * One opening, after the appointment types have been reconciled.
 *
 * The engine is probed once per appointment type, and the same physical chair
 * comes back in several of those answers: Hamblin's emergency and limited-exam
 * probes return byte-identical payloads. A slot is therefore a time with a list
 * of types that fit it, not one row per type.
 */
export type MergedSlot = {
  date: string
  /** The engine's own string, kept for the record. */
  time: string
  start: Minutes
  /** How many chairs are open at this time. */
  count: number
  /** Every appointment type the engine will book into this time. */
  types: { key: string; label: string }[]
  /**
   * The PMS name of the room this opening is in, when the caller knows it.
   *
   * The day board does, because `scheduling-data` carries the operatory on every
   * open time. The practice's own name for the room is the most specific thing
   * this surface can say about an opening, and it tells the staff what belongs in
   * it better than any category mapping of ours.
   */
  room?: string
  duration?: number | null
}

/** A chair, or a provider standing in for one until the backend carries chairs. */
export type GridColumn = {
  id: string
  label: string
  /** `provider` while `operatoryId` is missing from the Appointment model. */
  kind: `operatory` | `provider`
}

export type PlacedAppointment = {
  appointment: GridAppointment
  /** The chair, on the day the record starts naming one. */
  columnId?: string
  /**
   * Which vertical strip of the day this block sits in.
   *
   * Lanes are how many chairs are running at once, not which chair is which.
   * See `assignLanes`.
   */
  lane: number
  start: Minutes
  end: Minutes
}

export type PlacedSlot = {
  slot: MergedSlot
  start: Minutes
  end: Minutes
}

/** One slice of the day, and how much of the practice is running through it. */
export type LoadSlice = {
  start: Minutes
  end: Minutes
  /**
   * Appointments running at once. Never chairs: see `dayLoad` for why the
   * distinction is the whole point.
   */
  running: number
}

export type GridDay = {
  date: string
  appointments: PlacedAppointment[]
  slots: PlacedSlot[]
  /** How busy the day is, slice by slice, for the shading behind the openings. */
  load: LoadSlice[]
  /** How many chairs this day runs at its busiest. Zero on a day with nothing in it. */
  laneCount: number
  /**
   * A name per lane, once the records carry chairs. Empty while they do not,
   * which is the signal to the component to draw lanes unlabelled rather than
   * inventing a room name.
   */
  laneLabels: string[]
}

export type Grid = {
  days: GridDay[]
  columns: GridColumn[]
  /**
   * The busiest moment in the week, as the scale the shading is drawn against.
   * Taken across the week rather than per day so a quiet Friday reads quiet
   * instead of being rescaled until it looks as full as Monday.
   */
  peakLoad: number
  /** The drawn window, so the axis and the blocks agree on where the day starts. */
  startMinute: Minutes
  endMinute: Minutes
}

const DEFAULT_DURATION = 30
/** Fallback window when a day has nothing in it at all: 8am to 5pm. */
const DEFAULT_START = 8 * 60
const DEFAULT_END = 17 * 60

/**
 * Where an opening sits on the clock.
 *
 * The availability engine reports a time as a sentence, not a clock reading:
 * `"1:00 PM - Thursday, August 20, 2026"`. The day is already carried on the
 * slot, so only the leading time is read. Handles a bare `HH:mm` too, since the
 * same shape reaches this from tests and from anything the engine changes to.
 */
export function slotMinutes(time: string): Minutes | undefined {
  const head = (time ?? ``).split(` - `)[0].trim()
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])?\.?[Mm]?\.?$/.exec(head)
  if (!match) return undefined
  const mins = Number(match[2])
  let hours = Number(match[1])
  const meridiem = match[3]?.toLowerCase()
  if (meridiem) {
    // 12 AM is midnight and 12 PM is noon, so the hour wraps before it shifts.
    if (hours > 12 || hours < 1) return undefined
    hours = hours % 12
    if (meridiem === `p`) hours += 12
  }
  if (hours > 23 || mins > 59) return undefined
  return hours * 60 + mins
}

/**
 * Reconcile the per-type answers into one list of openings.
 *
 * Each appointment type is a separate call to the engine, and the same chair
 * comes back in more than one answer. Summing the counts would promise two
 * chairs at ten o'clock where there is one, which is the same class of lie as
 * inventing a gap. The count is therefore the widest any type reported, and the
 * types are gathered so the panel can say what will fit.
 */
export function mergeSlots(slots: GridSlot[]): MergedSlot[] {
  const merged = new Map<string, MergedSlot>()
  for (const slot of slots) {
    const start = slotMinutes(slot.time)
    // A time nobody can read cannot be offered to a patient.
    if (start === undefined) continue
    const key = `${slot.date}|${start}`
    const existing = merged.get(key)
    if (!existing) {
      merged.set(key, {
        date: slot.date,
        time: slot.time,
        start,
        count: slot.count,
        types: [{ key: slot.type, label: slot.typeLabel }],
        duration: slot.duration,
      })
      continue
    }
    existing.count = Math.max(existing.count, slot.count)
    // The longest booking the opening is known to hold. Drawing the shortest
    // would make a two-hour opening look like half an hour of dead time.
    if ((slot.duration ?? 0) > (existing.duration ?? 0)) existing.duration = slot.duration
    if (!existing.types.some((type) => type.key === slot.type)) {
      existing.types.push({ key: slot.type, label: slot.typeLabel })
    }
  }
  return [...merged.values()]
}

/**
 * Does this appointment occupy its chair?
 *
 * The treatment board's rule, for the same reason: a cancelled appointment is
 * not booked. Drawing one as occupied would hide the calendar's best gaps,
 * because a chair somebody just cancelled out of is the most fillable time the
 * practice has.
 */
export function occupiesChair(appointment: GridAppointment): boolean {
  return !appointment.cancelled && !appointment.broken
}

/**
 * Which column an appointment belongs to.
 *
 * Operatory when the record carries one, provider otherwise. Returns undefined
 * for a record with neither, which cannot be placed in any chair.
 */
export function columnIdFor(appointment: GridAppointment): string | undefined {
  return appointment.operatoryId || appointment.providerId || undefined
}

/**
 * The chairs the week can name, which is either all of them or none.
 *
 * The design allowed a fallback to provider columns while `Appointment` carries
 * no `operatoryId`. Measured against Hamblin on 2026-08-14, that fallback is
 * worse than nothing: 77 providers whose display names are bare numbers ("14",
 * "31", "0005-2"), nearly all deactivated, and 144 of 248 appointments in a
 * single week carrying two providers at once. Columns built from that would be
 * unreadable and wrong about who is where.
 *
 * So the week names chairs only when the records do, and draws unnamed lanes
 * otherwise. The layout is the same either way: see `assignLanes`.
 */
export function buildColumns(
  operatories: { id: string; label: string }[],
  appointments: GridAppointment[],
): GridColumn[] {
  const usesOperatories = appointments.some((a) => Boolean(a.operatoryId))
  if (!usesOperatories || !operatories.length) return []
  return operatories.map((o) => ({ id: o.id, label: o.label, kind: `operatory` as const }))
}

/**
 * The vertical window the week is drawn in.
 *
 * A working day at minimum, widened to whatever the real data needs, and padded
 * to the hour so the axis labels land on round numbers. A practice that opens
 * at 7 does not have its first hour clipped, and one that runs an evening does
 * not have it cut off.
 *
 * The minimum is what keeps two weeks comparable. Drawing only the hours a week
 * happens to contain meant a quiet week zoomed until one lunchtime appointment
 * filled the screen, so flicking between weeks changed the scale under the
 * reader every time.
 */
export function dayWindow(
  appointments: PlacedAppointment[],
  slots: PlacedSlot[],
): { startMinute: Minutes; endMinute: Minutes } {
  const starts = [...appointments.map((a) => a.start), ...slots.map((s) => s.start)]
  const ends = [...appointments.map((a) => a.end), ...slots.map((s) => s.end)]
  if (!starts.length || !ends.length) return { startMinute: DEFAULT_START, endMinute: DEFAULT_END }
  const start = Math.min(Math.floor(Math.min(...starts) / 60) * 60, DEFAULT_START)
  const end = Math.max(Math.ceil(Math.max(...ends) / 60) * 60, DEFAULT_END)
  return { startMinute: start, endMinute: Math.max(end, start + 60) }
}

type Span = { appointment: GridAppointment; columnId?: string; start: Minutes; end: Minutes }

function spansFor(appointments: GridAppointment[], columns: GridColumn[]): Span[] {
  const known = new Set(columns.map((c) => c.id))
  return appointments
    .flatMap((appointment) => {
      if (!occupiesChair(appointment)) return []
      const start = toMinutes(appointment.time)
      if (start === undefined) return []
      const columnId = columns.length ? columnIdFor(appointment) : undefined
      // Once the week is drawing named chairs, a chair it is not drawing cannot
      // hold a block: putting it in the first lane instead would show somebody
      // else's patient in the wrong room. With no chairs named there is no such
      // claim to get wrong, so every readable appointment is drawn.
      if (columns.length && (!columnId || !known.has(columnId))) return []
      const duration = appointment.duration && appointment.duration > 0 ? appointment.duration : DEFAULT_DURATION
      return [{ appointment, columnId, start, end: start + duration }]
    })
    .sort((a, b) => a.start - b.start || a.end - b.end)
}

/**
 * Which vertical strip each block sits in.
 *
 * Two shapes, one layout. While the appointment record names no chair, blocks
 * are packed into the fewest strips that hold them without overlapping, so the
 * width of a day is the number of chairs the practice was actually running at
 * its busiest: a thirteen-room Monday reads full, a six-room Friday reads
 * quiet, and nothing claims to know which room is which.
 *
 * Once `operatoryId` lands on the model the same strips become the chairs
 * themselves, one lane per room the day used, in the order the location lists
 * them. Only the labelling changes.
 */
function assignLanes(spans: Span[], columns: GridColumn[]): { placed: PlacedAppointment[]; labels: string[] } {
  if (columns.length) {
    // Only the rooms this day used. A practice with fourteen rooms running six
    // of them on a Friday should read as a six-room Friday, not as eight
    // empty chairs.
    const used = columns.filter((column) => spans.some((span) => span.columnId === column.id))
    const laneOf = new Map(used.map((column, index) => [column.id, index]))
    return {
      placed: spans.map((span) => ({ ...span, lane: laneOf.get(span.columnId ?? ``) ?? 0 })),
      labels: used.map((column) => column.label),
    }
  }
  // Greedy interval packing over blocks already sorted by start, which uses
  // exactly as many lanes as the busiest overlap. Back-to-back is not an
  // overlap: a ten o'clock finish and a ten o'clock start are one chair.
  const laneEnds: Minutes[] = []
  const placed = spans.map((span) => {
    let lane = laneEnds.findIndex((end) => end <= span.start)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = span.end
    return { ...span, lane }
  })
  return { placed, labels: [] }
}

function placeSlots(slots: GridSlot[]): PlacedSlot[] {
  return mergeSlots(slots).flatMap((slot) => {
    // Nothing open is not an opening. The engine reports zero-count times and
    // drawing them would fill the week with gaps nobody can book into.
    if (!slot.count || slot.count < 1) return []
    const duration = slot.duration && slot.duration > 0 ? slot.duration : DEFAULT_DURATION
    return [{ slot, start: slot.start, end: slot.start + duration }]
  })
}

/** How fine the shading is cut. Half an hour is the practice's own booking unit. */
const LOAD_SLICE = 30

/**
 * How busy the practice is through a day.
 *
 * The figure is **appointments running at once, never chairs**. Hamblin has
 * fourteen operatories, of which two are administrative parking buckets
 * (`Inv/PO/Adj/NG`, `Canclled/Tx Follow Up/NG`) and one is our own agent's
 * ghost chair. Nothing in the data separates those from real rooms: volume
 * cannot, because the buckets are actively used, and neither can structure,
 * because `short_code` is empty and a resource carries only the practice
 * address. Measured on the week of 2026-08-10, calling the buckets chairs moves
 * the busiest Tuesday from 8 to 11, so a chair claim would be a quarter out on
 * a real day.
 *
 * Counting appointments needs no census at all, which is what lets the week
 * ship without the chair configuration that a day drill-down will need.
 */
export function dayLoad(appointments: PlacedAppointment[], startMinute: Minutes, endMinute: Minutes): LoadSlice[] {
  const slices: LoadSlice[] = []
  for (let start = startMinute; start < endMinute; start += LOAD_SLICE) {
    const end = Math.min(start + LOAD_SLICE, endMinute)
    slices.push({
      start,
      end,
      // Strict overlap, so an appointment that ends as the next begins is one
      // chair being reused rather than two running at once.
      running: appointments.filter((placed) => placed.start < end && placed.end > start).length,
    })
  }
  return slices
}

/**
 * Build the week.
 *
 * `dates` drives the columns of days rather than the data, so a day with
 * nothing in it still gets drawn. A missing Wednesday would otherwise silently
 * shift Thursday into its place.
 */
export function buildGrid(input: {
  dates: string[]
  appointments: GridAppointment[]
  slots: GridSlot[]
  operatories: { id: string; label: string }[]
}): Grid {
  const columns = buildColumns(input.operatories, input.appointments)
  const days: GridDay[] = input.dates.map((date) => {
    const { placed, labels } = assignLanes(
      spansFor(
        input.appointments.filter((a) => a.date === date),
        columns,
      ),
      columns,
    )
    return {
      date,
      appointments: placed,
      slots: placeSlots(input.slots.filter((s) => s.date === date)),
      // Filled once the drawn window is known, below.
      load: [] as LoadSlice[],
      laneCount: placed.reduce((widest, block) => Math.max(widest, block.lane + 1), 0),
      laneLabels: labels,
    }
  })
  const { startMinute, endMinute } = dayWindow(
    days.flatMap((d) => d.appointments),
    days.flatMap((d) => d.slots),
  )
  // The shading has to span exactly what the axis draws, so it is cut after the
  // window is settled rather than guessed at per day.
  for (const day of days) day.load = dayLoad(day.appointments, startMinute, endMinute)
  const peakLoad = days.reduce((peak, day) => Math.max(peak, ...day.load.map((slice) => slice.running)), 0)
  return { days, columns, startMinute, endMinute, peakLoad }
}

/** Open slots in a day, soonest first, for the list beside the grid. */
export function openingsFor(day: GridDay): PlacedSlot[] {
  return [...day.slots].sort((a, b) => a.start - b.start)
}

/** Total openings in the week, for the header count. */
export function totalOpenings(grid: Grid): number {
  return grid.days.reduce((sum, day) => sum + day.slots.reduce((n, s) => n + s.slot.count, 0), 0)
}
